import FieldType from "../../../consts/datasourcemodule/FieldType";

// Valeur constante d'une condition de filtre simple, typée d'après le FieldType du champ.
//
// Stockage (colonne FilterParameterValue.Value côté back) : un SCALAIRE JSON —
//   String / Date / DateTime / DateTimeOffset -> chaîne JSON  ("abc", "2026-09-09", "2026-09-09T14:30:00")
//   Int32 / BigInt / Decimal / Double         -> nombre JSON   (42, 3.14)
//   Boolean                                   -> booléen JSON  (true / false)
//   (non renseigné)                           -> "" (colonne vide)
//
// Dates : jamais de conversion UTC (pas de toISOString). Heure murale, getters locaux.
//   Date           -> "yyyy-MM-dd"
//   DateTime       -> "yyyy-MM-ddTHH:mm:ss"
//   DateTimeOffset -> "yyyy-MM-ddTHH:mm:ss±hh:mm"

export type FilterConstantKind =
    | "empty"
    | "string"
    | "number"
    | "boolean"
    | "date"
    | "datetime"
    | "datetimeoffset";

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_LEADING_RE = /^(\d{4})-(\d{2})-(\d{2})/;

function pad(n: number, width = 2): string {
    return Math.abs(n).toString().padStart(width, "0");
}

function localDate(d: Date): string {
    return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localTime(d: Date): string {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function localOffset(d: Date): string {
    // getTimezoneOffset : minutes derrière UTC (positif si en retard). +02:00 -> -120.
    const minutes = -d.getTimezoneOffset();
    const sign = minutes >= 0 ? "+" : "-";
    return `${sign}${pad(Math.floor(Math.abs(minutes) / 60))}:${pad(Math.abs(minutes) % 60)}`;
}

const DATETIME_LEADING_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

// Locale active = celle choisie par l'app (App.tsx : locale(navigator.language)).
function browserLocale(): string | undefined {
    return typeof navigator !== "undefined" && navigator.language ? navigator.language : undefined;
}

// Rend une valeur date/heure canonique au format régional du navigateur (ordre, séparateur, année
// pleine — comme "shortDate" DevExtreme mais indépendant de la config de localisation DevExtreme).
function formatRegionalDate(canonical: string, withTime: boolean): string {
    const d = parseLoose(canonical);
    if (d == null) {
        return canonical;
    }
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
    if (withTime) {
        options.hour = "2-digit";
        options.minute = "2-digit";
    }
    try {
        return new Intl.DateTimeFormat(browserLocale(), options).format(d);
    } catch {
        return canonical;
    }
}

// Chaîne date/heure "locale" (sans offset, ex. "2026-09-09" ou "2026-09-09T14:30:00") -> Date locale.
// Une chaîne avec offset / "Z" est déléguée au parseur natif (instant absolu).
function parseLoose(value: string): Date | null {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return null;
    }
    if (DATE_ONLY_RE.test(trimmed)) {
        const [y, m, d] = trimmed.split("-").map(Number);
        return new Date(y, m - 1, d);
    }
    const naive = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
    if (naive) {
        const [, y, mo, d, h, mi, s] = naive;
        return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? "0"));
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export default class FilterConstantValue {
    private constructor(
        readonly kind: FilterConstantKind,
        readonly raw: string | number | boolean | null,
    ) {}

    static kindOf(fieldType: number | undefined): FilterConstantKind {
        switch (fieldType) {
            case FieldType.Int32:
            case FieldType.BigInt:
            case FieldType.Decimal:
            case FieldType.Double:
                return "number";
            case FieldType.Boolean:
                return "boolean";
            case FieldType.Date:
                return "date";
            case FieldType.DateTime:
                return "datetime";
            case FieldType.DateTimeOffset:
                return "datetimeoffset";
            default:
                return "string";
        }
    }

    static empty(): FilterConstantValue {
        return new FilterConstantValue("empty", null);
    }

    // Scalaire JSON stocké (ou ancienne valeur en clair, tolérée) -> instance typée. Ne lève jamais.
    static parse(stored: string | null | undefined, fieldType: number | undefined): FilterConstantValue {
        if (stored == null || stored === "") {
            return FilterConstantValue.empty();
        }
        let decoded: unknown = stored;
        try {
            decoded = JSON.parse(stored);
        } catch {
            // Ancienne donnée écrite en clair (avant la bascule) : on retombe sur la chaîne brute.
            decoded = stored;
        }
        return FilterConstantValue.fromInput(decoded, fieldType);
    }

    // Sortie d'un éditeur DevExtreme (FilterBuilder / DateBox / NumberBox / SelectBox) ou chaîne
    // d'édition du FilterPopup -> instance normalisée selon le type du champ.
    static fromInput(value: unknown, fieldType: number | undefined): FilterConstantValue {
        const kind = FilterConstantValue.kindOf(fieldType);

        if (value == null || value === "") {
            return FilterConstantValue.empty();
        }

        switch (kind) {
            case "number": {
                const n = typeof value === "number" ? value : Number(value);
                return Number.isFinite(n) ? new FilterConstantValue("number", n) : FilterConstantValue.empty();
            }
            case "boolean": {
                const b = typeof value === "boolean" ? value : String(value).toLowerCase() === "true";
                return new FilterConstantValue("boolean", b);
            }
            case "date":
            case "datetime":
            case "datetimeoffset": {
                const normalized = FilterConstantValue.normalizeDate(value, kind);
                return normalized == null ? FilterConstantValue.empty() : new FilterConstantValue(kind, normalized);
            }
            default:
                return new FilterConstantValue("string", String(value));
        }
    }

    private static normalizeDate(value: unknown, kind: "date" | "datetime" | "datetimeoffset"): string | null {
        // Chaîne déjà au bon format canonique : on la garde telle quelle (pas d'aller-retour Date qui
        // risquerait un décalage de jour selon le fuseau).
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (kind === "date" && DATE_ONLY_RE.test(trimmed)) {
                return trimmed;
            }
            if (kind === "date") {
                const m = DATE_LEADING_RE.exec(trimmed);
                if (m) {
                    return `${m[1]}-${m[2]}-${m[3]}`;
                }
            }
        }

        const d = value instanceof Date ? value : typeof value === "string" ? parseLoose(value) : null;
        if (d == null || Number.isNaN(d.getTime())) {
            return null;
        }
        if (kind === "date") {
            return localDate(d);
        }
        if (kind === "datetime") {
            return `${localDate(d)}T${localTime(d)}`;
        }
        return `${localDate(d)}T${localTime(d)}${localOffset(d)}`;
    }

    get isEmpty(): boolean {
        return this.kind === "empty" || this.raw == null;
    }

    // Scalaire JSON à stocker dans FilterParameterValueDto.Value. "" si vide.
    // JSON.stringify d'un primitif est canonique -> comparaison string == fiable côté back.
    toJson(): string {
        return this.isEmpty ? "" : JSON.stringify(this.raw);
    }

    // Valeur à passer au FilterBuilder DevExtreme (il reformate les dates via le `dataType` du champ).
    toFilterBuilderValue(): string | number | boolean | null {
        return this.isEmpty ? null : this.raw;
    }

    // Chaîne d'édition interne du FilterPopup (EditBlock.value).
    toEditString(): string {
        if (this.isEmpty) {
            return "";
        }
        if (this.kind === "boolean") {
            return this.raw ? "true" : "false";
        }
        return String(this.raw);
    }

    // Texte affiché dans la pastille d'une condition / résumé : dates au format régional du navigateur.
    toDisplayText(): string {
        if (this.isEmpty) {
            return "";
        }
        if (this.kind === "date") {
            return formatRegionalDate(String(this.raw), false);
        }
        if (this.kind === "datetime" || this.kind === "datetimeoffset") {
            return formatRegionalDate(String(this.raw), true);
        }
        return String(this.raw);
    }

    // Affichage sans connaître le FieldType (résumé de colonne) : retire les guillemets JSON d'une
    // chaîne, formate les dates détectées au format régional, retombe sur la valeur brute si non-JSON.
    static displayLoose(stored: string | null | undefined): string {
        if (stored == null || stored === "") {
            return "";
        }
        let v: unknown;
        try {
            v = JSON.parse(stored);
        } catch {
            return stored;
        }
        if (v == null || typeof v === "object") {
            return stored;
        }
        if (typeof v === "string") {
            if (DATE_ONLY_RE.test(v)) {
                return formatRegionalDate(v, false);
            }
            if (DATETIME_LEADING_RE.test(v)) {
                return formatRegionalDate(v, true);
            }
            return v;
        }
        return String(v);
    }
}
